import type { ReactNode } from "react";
import { Calendar20Regular, Edit20Regular } from "@fluentui/react-icons";
import type { Work } from "../../../obras/obrasApi";
import {
  formatCurrency,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatPercent,
} from "../obraFormat";

interface WorkSummaryDashboardProps {
  work: Work;
  clientName: string;
  statusLabel: string;
  valorAdjudicado: number | null;
  // [4.8.1] Orçamento revisto (base + variações aprovadas). Opcional para não
  // quebrar call sites sem variações; mostrado só quando difere do adjudicado.
  revisedSell?: number | null;
  baseSell?: number | null;
  custoTotalAtual: number | null;
  margemPrevista: number | null;
  progresso: number;
  dataInicio: string | null;
  dataFim: string | null;
  custoSubempreiteiros: number | null;
  custoMateriais: number | null;
  custoEquipaInterna: number | null;
  onEditObra: () => void;
  budgetSlot: ReactNode;
  commentsSlot: ReactNode;
}

const cardClass = "bg-card border border-border p-5";
const sectionCardClass = "bg-card border border-border p-6";
const captionStyle = { fontSize: "var(--text-caption)" } as const;
const h3Value = {
  fontSize: "var(--text-h3)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;
const sectionTitleStyle = {
  fontSize: "var(--text-section-title)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-secondary-text mb-1" style={captionStyle}>
        {label}
      </p>
      <p className="text-foreground" style={{ fontSize: "var(--text-base)" }}>
        {value}
      </p>
    </div>
  );
}

export function WorkSummaryDashboard({
  work,
  clientName,
  statusLabel,
  valorAdjudicado,
  revisedSell,
  custoTotalAtual,
  margemPrevista,
  progresso,
  dataInicio,
  dataFim,
  custoSubempreiteiros,
  custoMateriais,
  custoEquipaInterna,
  onEditObra,
  budgetSlot,
  commentsSlot,
}: WorkSummaryDashboardProps) {
  return (
    <div className="space-y-6">
      {/* Top action row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-secondary-text text-sm">
          Atualizado em {formatDateTime(work.lastModified ?? "")}
        </span>
        <button
          type="button"
          onClick={onEditObra}
          className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          <Edit20Regular className="w-5 h-5" />
          Editar obra
        </button>
      </div>

      {/* KPI hero strip (read-only) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <p className="text-secondary-text mb-2" style={captionStyle}>Valor Adjudicado</p>
          <p className="text-foreground" style={h3Value}>{formatCurrency(valorAdjudicado)}</p>
          {revisedSell != null && revisedSell !== valorAdjudicado && (
            <p className="text-secondary-text mt-1" style={captionStyle}>
              Revisto: {formatCurrency(revisedSell)}
            </p>
          )}
        </div>
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <p className="text-secondary-text mb-2" style={captionStyle}>Custo Total Atual</p>
          <p className="text-foreground" style={h3Value}>{formatCurrency(custoTotalAtual)}</p>
        </div>
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <p className="text-secondary-text mb-2" style={captionStyle}>Margem Prevista</p>
          <p className="text-primary" style={h3Value}>{formatPercent(margemPrevista)}</p>
        </div>
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <p className="text-secondary-text mb-2" style={captionStyle}>Progresso da Obra</p>
          <div className="flex items-center gap-3">
            <span className="text-foreground" style={h3Value}>{Math.round(progresso)}%</span>
            <div className="flex-1">
              <div className="w-full bg-muted rounded-full h-2" style={{ borderRadius: "var(--radius)" }}>
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${progresso}%`, borderRadius: "var(--radius)" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informação da Obra (read-only) */}
      <div className={sectionCardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>Informação da Obra</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Cliente" value={clientName || "—"} />
          <Field label="Código da Obra" value={work.code || "—"} />
          <Field label="Nome da Obra" value={work.name || "—"} />
          <Field label="Local" value={work.location || "—"} />
          <Field label="Estado" value={statusLabel} />
        </div>
      </div>

      {/* Datas (read-only) */}
      <div className={sectionCardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>Datas Importantes</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field
            label="Data de Início"
            value={
              <span className="inline-flex items-center gap-2">
                <Calendar20Regular className="w-4 h-4 text-secondary-text" />
                {formatDateShort(dataInicio)}
              </span>
            }
          />
          <Field
            label="Data Prevista de Conclusão"
            value={
              <span className="inline-flex items-center gap-2">
                <Calendar20Regular className="w-4 h-4 text-secondary-text" />
                {formatDateShort(dataFim)}
              </span>
            }
          />
          <Field label="Duração Estimada" value={formatDuration(dataInicio, dataFim)} />
        </div>
      </div>

      {/* Budget versions (existing card, injected) */}
      {budgetSlot}

      {/* Execução Global (read-only) */}
      <div className={sectionCardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>Execução Global</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Sub-empreiteiros" value={formatCurrency(custoSubempreiteiros)} />
          <Field label="Materiais (Fornecedores)" value={formatCurrency(custoMateriais)} />
          <Field label="Equipa Interna" value={formatCurrency(custoEquipaInterna)} />
        </div>
      </div>

      {/* Comments (existing, injected) */}
      {commentsSlot}
    </div>
  );
}
